/**
 * Client callable telemetry — pure helpers (2026-10-28).
 *
 * Why: on 2026-10-27 a recruiter bug ("click the red X three times") could
 * only be diagnosed from Cloud Run request logs (a 15 s cold-start preflight,
 * duplicate POSTs, then a 404) because the web client folds every
 * `httpsCallable` failure into a generic banner and records nothing. These
 * helpers decide what `src/utils/callable.ts` (`callFn`) records about each
 * call so the next complaint is diagnosable from our own data.
 *
 * Everything here is pure (no Firebase, no DOM) and unit-tested:
 *   - sampling: always on failure, always when slow (> SLOW_MS), else 1 in 20
 *   - scrubbing: error message capped at 200 chars with emails / phone numbers
 *     removed — never the request payload, never the response
 *   - shaping: the exact field set the firestore.rules allowlist accepts for
 *     `tenants/{t}/client_events` (anything else is denied server-side too)
 *
 * The runtime wrapper adds uid / tenant / serverTimestamp and writes
 * fire-and-forget. Rollup: functions/src/ops/callableTelemetryDigest.ts.
 */
import { normalizeRoute } from './permissionTelemetry/core';

export const CLIENT_EVENTS_COLLECTION = 'client_events';
/** A call slower than this is always recorded (and flagged by the digest). */
export const SLOW_MS = 5000;
/** Fast successes are recorded at this rate (1 in 20). */
export const SUCCESS_SAMPLE_RATE = 1 / 20;

export const MAX_NAME = 80;
export const MAX_CODE = 40;
export const MAX_ROUTE = 120;
export const MAX_VERSION = 40;
export const MAX_UA = 20;
export const MAX_MESSAGE = 200;
/** Latency is clamped so the rules `int` range check always holds (10 min). */
export const MAX_MS = 600_000;

export type BrowserFamily = 'chrome' | 'safari' | 'firefox' | 'edge' | 'samsung' | 'opera' | 'other' | 'unknown';

/**
 * Browser FAMILY only (never the full user-agent string — it is close to a
 * fingerprint). Order matters: Edge/Opera/Samsung ship "Chrome" in their UA,
 * and Chrome on iOS ships "Safari".
 */
export function browserFamily(ua: unknown): BrowserFamily {
  if (typeof ua !== 'string' || !ua.trim()) return 'unknown';
  const s = ua.toLowerCase();
  if (s.includes('edg/') || s.includes('edge/') || s.includes('edga/') || s.includes('edgios/')) return 'edge';
  if (s.includes('opr/') || s.includes('opera')) return 'opera';
  if (s.includes('samsungbrowser')) return 'samsung';
  if (s.includes('firefox/') || s.includes('fxios/')) return 'firefox';
  if (s.includes('chrome/') || s.includes('crios/') || s.includes('chromium/')) return 'chrome';
  if (s.includes('safari/') && s.includes('version/')) return 'safari';
  if (s.includes('applewebkit') && (s.includes('iphone') || s.includes('ipad'))) return 'safari';
  return 'other';
}

export interface SampleInput {
  ok: boolean;
  ms: number;
  /** A draw in [0, 1); injected so the decision is testable. */
  random: number;
}

/** Always on failure, always when slow, else 1 in 20 of fast successes. */
export function shouldSample({ ok, ms, random }: SampleInput): boolean {
  if (!ok) return true;
  if (!(ms < SLOW_MS)) return true; // NaN / Infinity count as slow, never as "fast"
  const r = Number.isFinite(random) ? random : 1;
  return r < SUCCESS_SAMPLE_RATE;
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/**
 * 7+ digits with optional separators and an optional leading + or ( —
 * catches US/intl phones and most account numbers. ISO dates / timestamps
 * (group 1) are matched first and kept: "2026-10-27" is not a phone.
 */
const PHONE_RE = /(\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?)|(\+?\(?\d[\d\s().-]{5,}\d)/g;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]+/g;

/**
 * Error message safe to store: emails and phone-shaped digit runs replaced,
 * control characters collapsed to spaces, then capped at MAX_MESSAGE.
 * Scrub BEFORE truncating so a cut-off email never survives at the edge.
 */
export function scrubMessage(raw: unknown): string {
  if (raw == null) return '';
  const text = typeof raw === 'string' ? raw : String(raw);
  const scrubbed = text
    .replace(CONTROL_RE, ' ')
    .replace(EMAIL_RE, '[email]')
    .replace(PHONE_RE, (m, isoDate?: string) => (isoDate ? m : m.replace(/\D/g, '').length >= 7 ? '[phone]' : m))
    .replace(/\s{2,}/g, ' ')
    .trim();
  return scrubbed.slice(0, MAX_MESSAGE);
}

/**
 * The SDK error code ('functions/not-found', 'functions/deadline-exceeded',
 * …) or a best-effort bucket when the throw was not a FirebaseError.
 */
export function errorCode(err: unknown): string {
  if (err && typeof err === 'object') {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string' && code.trim()) return code.trim().toLowerCase().slice(0, MAX_CODE);
    const name = (err as { name?: unknown }).name;
    if (name === 'AbortError') return 'client/aborted';
    if (name === 'TypeError') return 'client/network'; // fetch() failure (offline, CORS preflight died)
  }
  return 'unknown';
}

export function errorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  if (typeof err === 'string') return err;
  return '';
}

/** Build id for the record: explicit REACT_APP_VERSION, else the craco-injected git sha. */
export function resolveAppVersion(env: Record<string, string | undefined> | undefined): string {
  const v = env?.REACT_APP_VERSION || env?.REACT_APP_BUILD_SHA || 'dev';
  return String(v).slice(0, MAX_VERSION);
}

/** Exactly the fields firestore.rules allows on tenants/{t}/client_events (plus `at`, added by the writer). */
export interface CallableEvent {
  kind: 'callable';
  name: string;
  ms: number;
  ok: boolean;
  code: string;
  route: string;
  uid: string;
  appVersion: string;
  ua: BrowserFamily;
  /** Scrubbed, capped error message — failures only. */
  msg?: string;
}

export interface ShapeInput {
  name: string;
  ms: number;
  ok: boolean;
  code?: string;
  msg?: string;
  /** Raw pathname or an explicit route label; ids are replaced with ':id'. */
  route: string | null | undefined;
  uid: string;
  appVersion: string;
  ua: BrowserFamily;
}

const clampMs = (ms: number): number => {
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return Math.min(MAX_MS, Math.round(ms));
};

/** Clamp / truncate every field so a record can never be rejected for size, and never carries a payload. */
export function shapeEvent(input: ShapeInput): CallableEvent {
  const ok = input.ok === true;
  const code = (ok ? 'ok' : input.code || 'unknown').slice(0, MAX_CODE) || 'unknown';
  const event: CallableEvent = {
    kind: 'callable',
    name: String(input.name || 'unknown').slice(0, MAX_NAME) || 'unknown',
    ms: clampMs(input.ms),
    ok,
    code,
    route: normalizeRoute(input.route).slice(0, MAX_ROUTE),
    uid: String(input.uid),
    appVersion: String(input.appVersion || 'dev').slice(0, MAX_VERSION),
    ua: input.ua,
  };
  if (!ok) {
    const msg = scrubMessage(input.msg);
    if (msg) event.msg = msg;
  }
  return event;
}
